import {
  Box,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Progress,
  Spinner,
  Text,
  VStack,
  keyframes,
  useColorModeValue,
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { motion, AnimatePresence } from 'framer-motion';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

export type ContractPaymentStep =
  | 'idle'
  | 'validating' // appel /sale/purchasecontract en cours
  | 'authenticating' // redirection 3DS imminente / en cours
  | 'finalizing' // retour SCA, /sale/scacomplete-contract en cours
  | 'success'
  | 'failed';

interface StepConfig {
  title: string;
  description: string;
  showProgress: boolean;
}

const STEP_CONFIGS: Record<ContractPaymentStep, StepConfig> = {
  idle: { title: '', description: '', showProgress: false },
  validating: {
    title: 'Validation de votre paiement bancaire',
    description: 'Nous contactons votre banque pour autoriser le premier prélèvement…',
    showProgress: true,
  },
  authenticating: {
    title: 'Authentification 3D Secure',
    description: 'Vous allez être redirigé(e) vers la page d\'authentification de votre banque.',
    showProgress: true,
  },
  finalizing: {
    title: 'Création de votre abonnement',
    description: 'Nous enregistrons votre carte pour les prélèvements automatiques mensuels…',
    showProgress: true,
  },
  success: {
    title: 'Abonnement activé !',
    description: 'Votre abonnement peut apparaître dans Mon Compte d\'ici 1 minute.',
    showProgress: false,
  },
  failed: {
    title: "Échec de la souscription",
    description: "Votre paiement n'a pas pu être validé. Aucun montant n'a été prélevé.",
    showProgress: false,
  },
};

export interface ContractPaymentOverlayProps {
  isOpen: boolean;
  currentStep: ContractPaymentStep;
  errorMessage?: string | null;
}

/**
 * Overlay full-screen affiché pendant le flow d'achat d'un contract pour
 * rassurer l'utilisateur ("Ne fermez pas la page"). Calqué sur
 * AlmaPaymentOverlay mais sans logique de polling/cancel : le flow contract
 * est synchrone (un appel HTTP, redirection 3DS si besoin, retour SCA).
 */
export function ContractPaymentOverlay({ isOpen, currentStep, errorMessage }: ContractPaymentOverlayProps) {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const config = STEP_CONFIGS[currentStep];

  const renderIcon = () => {
    if (currentStep === 'success') {
      return (
        <MotionBox initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }}>
          <Box w={16} h={16} borderRadius="full" bg="green.100" display="flex" alignItems="center" justifyContent="center">
            <CheckIcon w={8} h={8} color="green.600" />
          </Box>
        </MotionBox>
      );
    }
    if (currentStep === 'failed') {
      return (
        <MotionBox initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }}>
          <Box w={16} h={16} borderRadius="full" bg="red.50" display="flex" alignItems="center" justifyContent="center">
            <CloseIcon w={6} h={6} color="red.600" />
          </Box>
        </MotionBox>
      );
    }
    return (
      <Box
        w={16}
        h={16}
        borderRadius="full"
        bg="orange.50"
        display="flex"
        alignItems="center"
        justifyContent="center"
        animation={`${pulse} 2s ease-in-out infinite`}
      >
        <Spinner size="lg" color="orange.500" thickness="3px" speed="0.8s" />
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={() => {}} isCentered closeOnOverlayClick={false} closeOnEsc={false} size="md">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent bg={bgColor} border="1px solid" borderColor={borderColor} borderRadius="2xl" boxShadow="2xl" mx={4}>
        <ModalBody p={8}>
          <AnimatePresence mode="wait">
            <MotionVStack
              key={currentStep}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              spacing={6}
            >
              <VStack spacing={4}>
                {renderIcon()}
                <VStack spacing={2} textAlign="center">
                  <Heading size="lg" color="gray.800">
                    {config.title}
                  </Heading>
                  <Text color="gray.600" fontSize="md">
                    {config.description}
                  </Text>
                </VStack>
              </VStack>

              {config.showProgress && (
                <Box w="full">
                  <Progress isIndeterminate colorScheme="orange" bg="gray.100" borderRadius="full" size="sm" hasStripe />
                </Box>
              )}

              {(currentStep === 'validating' || currentStep === 'finalizing' || currentStep === 'authenticating') && (
                <Text fontSize="sm" color="gray.500" textAlign="center" lineHeight="1.5">
                  Merci de <strong>ne pas fermer ni rafraîchir cette page</strong> pendant le traitement.
                </Text>
              )}

              {currentStep === 'failed' && errorMessage && (
                <Box bg="red.50" p={3} borderRadius="md" border="1px solid" borderColor="red.200" w="full">
                  <Text fontSize="sm" color="red.700">
                    {errorMessage}
                  </Text>
                </Box>
              )}
            </MotionVStack>
          </AnimatePresence>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
